import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import userAPI from "../../api/UserApi";
import { useSiteConfig } from "../../context/SiteConfigContext";
import { Download, ArrowLeft } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function OrderDetails() {
  const { config }   = useSiteConfig();
  const { id }       = useParams();
  const navigate     = useNavigate();

  const [order,      setOrder]      = useState({});
  const [orderItems, setOrderItems] = useState([]);

  if (!id) {
    navigate("/");
  }

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const res = await userAPI.orderDetails(id);
        if (res.code === 200) {
          setOrder(res.data?.order);
          setOrderItems(res.data?.orderItems);
        }
      } catch (err) {}
    };
    fetchOrderDetails();
  }, [id]);

  const handleDownload = async (orderId) => {
    const element = document.querySelector(".invoice-wrapper");
    window.scrollTo(0, 0);
    await new Promise((resolve) => setTimeout(resolve, 500));

    const canvas  = await html2canvas(element, { scale: 2, useCORS: true, allowTaint: false });
    const imgData = canvas.toDataURL("image/png");
    const pdf     = new jsPDF("p", "mm", "a4");
    const pageWidth  = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps   = pdf.getImageProperties(imgData);
    const pdfWidth   = pageWidth;
    const pdfHeight  = (imgProps.height * pdfWidth) / imgProps.width;

    if (pdfHeight > pageHeight) {
      let position = 0;
      while (position < pdfHeight) {
        pdf.addImage(imgData, "PNG", 0, -position, pdfWidth, pdfHeight);
        position += pageHeight;
        if (position < pdfHeight) pdf.addPage();
      }
    } else {
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    }

    pdf.save(orderId + ".pdf");
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <p className="vp-eyebrow">Order</p>
          <h2 className="vp-title" style={{ fontSize: "1.5rem" }}>
            Invoice #{order?.orderId}
          </h2>
        </div>
        <div className="d-flex gap-2">
          <button
            className="vp-btn-ghost"
            onClick={() => navigate("/users/orders")}
          >
            <ArrowLeft size={11} /> Back
          </button>
          <button
            className="vp-btn-primary"
            onClick={() => handleDownload(order?.orderId)}
          >
            <Download size={13} /> Download PDF
          </button>
        </div>
      </div>
      <div className="vp-rule" />

      {/* ── Invoice (capture target) ── */}
      <div className="invoice-wrapper">
        <div
          className="invoice-box"
          style={{
            background: "#fff",
            border: "1px solid var(--border-light)",
            borderRadius: "13px",
            padding: "2rem",
          }}
        >
          {/* Invoice Header */}
          <div
            className="invoice-header d-flex justify-content-between align-items-start flex-wrap mb-4"
            style={{ paddingBottom: "1.4rem", borderBottom: "1px solid var(--border-light)" }}
          >
            <div className="company-info">
              <img
                src={config?.logo}
                className="mb-2"
                alt="Logo"
                style={{ maxWidth: 90 }}
              />
              <h5
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "1rem",
                  marginBottom: "0.25rem",
                }}
              >
                {config?.name}
              </h5>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginBottom: "0.1rem" }}>
                <a
                  href={`mailto:${config?.email}`}
                  style={{ color: "var(--color-accent-main)", textDecoration: "none" }}
                >
                  {config?.email}
                </a>
              </p>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                <a
                  href={`tel:${config?.phone}`}
                  style={{ color: "var(--color-accent-main)", textDecoration: "none" }}
                >
                  +91-{config?.phone}
                </a>
              </p>
            </div>

            <div className="text-md-end">
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "0.68rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "var(--color-accent-main)",
                  marginBottom: "0.2rem",
                }}
              >
                Invoice
              </p>
              <h5
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "1.1rem",
                  marginBottom: "0.2rem",
                }}
              >
                #{order?.orderId}
              </h5>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                {order?.orderDate}
              </p>
            </div>
          </div>

          {/* Bill To */}
          <div
            className="client-info mb-4"
            style={{
              background: "var(--bg-secondary)",
              borderRadius: "9px",
              padding: "1rem 1.2rem",
            }}
          >
            <p
              style={{
                fontFamily: "var(--font-subheading)",
                fontSize: "0.72rem",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: "var(--color-text-muted)",
                marginBottom: "0.5rem",
              }}
            >
              Bill To
            </p>
            <p style={{ fontSize: "0.84rem", color: "var(--color-text-secondary)", lineHeight: "1.7", marginBottom: 0 }}>
              {order?.address?.firstName} {order?.address?.lastName}
              <br />
              {order?.address?.addressLine1}
              {order?.address?.addressLine2 ? " " + order?.address?.addressLine2 + "," : ""}
              <br />
              {order?.address?.district}, {order?.address?.state} —{" "}
              {order?.address?.pinCode} {order?.address?.country}
              <br />
              {order.email ? order.email + ", " : ""}{order?.phone}
            </p>
          </div>

          {/* Items Table */}
          <div className="table-responsive mb-4">
            <table
              className="table table-bordered text-center align-middle"
              style={{ fontSize: "0.85rem" }}
            >
              <thead style={{ background: "var(--bg-accent)", color: "#fff" }}>
                <tr>
                  <th>Item</th>
                  <th>Price</th>
                  <th>Qty</th>
                  <th>Total</th>
                </tr>
              </thead>
              {orderItems && (
                <tbody>
                  {orderItems.map((orderItem, index) => (
                    <tr key={index}>
                      <td className="text-start">
                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2">
                          <img
                            src={orderItem.productId.thumbnail}
                            style={{ width: 56, height: "auto", borderRadius: 6 }}
                            alt="Product"
                          />
                          <div>
                            <strong>{orderItem.productName}</strong>
                            <br />
                            <small style={{ color: "var(--color-text-muted)" }}>
                              {orderItem.variantName}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>₹{orderItem.price.toFixed(2)}/-</td>
                      <td>{orderItem.quantity}</td>
                      <td className="text-end">
                        ₹{(orderItem.price * orderItem.quantity).toFixed(2)}/-
                      </td>
                    </tr>
                  ))}

                  <tr>
                    <td colSpan={3} className="text-end fw-bold">SubTotal:</td>
                    <td className="fw-bold text-end">₹{order.subtotal}/-</td>
                  </tr>

                  {order.discount > 0 && (
                    <tr>
                      <td colSpan={3} className="text-end fw-bold">Discount:</td>
                      <td className="fw-bold text-end" style={{ color: "#2e7d32" }}>
                        (-) ₹{order.discount}/-
                      </td>
                    </tr>
                  )}

                  {order.shipping > 0 && (
                    <tr>
                      <td colSpan={3} className="text-end fw-bold">Shipping:</td>
                      <td className="fw-bold text-end">₹{order.shipping}/-</td>
                    </tr>
                  )}

                  <tr style={{ background: "var(--bg-secondary)" }}>
                    <td colSpan={3} className="text-end fw-bold">Total:</td>
                    <td
                      className="fw-bold text-end"
                      style={{ fontFamily: "var(--font-heading)", fontSize: "1rem" }}
                    >
                      ₹{order.total}/-
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>

          <div
            className="text-center"
            style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}
          >
            Thank you for your purchase.
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;